import {
  Badge,
  Button,
  buttonVariants,
  Card,
  CardContent,
  cn,
  EmptyState,
  formatYearMonth,
  MonthGrid,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tooltip,
  useToast,
} from "@forge/ui";
import { useMutation, useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Calendar, CircleDollarSign, FileSpreadsheet } from "lucide-react";
import { useState } from "react";
import { PageHeader } from "../../components/page-header";
import {
  ProfitQualityBadge,
  ProfitReadiness,
} from "../../components/profit-readiness";
import { api, unwrap } from "../../lib/api";
import { downloadAuthenticated } from "../../lib/download";
import { formatBRL, formatPercent } from "../../lib/format";

export const Route = createFileRoute("/_app/profit")({
  staticData: { layout: "wide" },
  component: ProfitPage,
});

function currentYearMonth() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

const ABC_VARIANT: Record<string, "accent" | "neutral" | "warning"> = {
  A: "accent",
  B: "neutral",
  C: "warning",
};

function ProfitPage() {
  const toast = useToast();
  const [month, setMonth] = useState(currentYearMonth);

  const report = useQuery({
    queryKey: ["profit", month],
    queryFn: async () =>
      unwrap(await api.orders.profit.get({ query: { month } })),
  });

  const exportCsv = useMutation({
    mutationFn: () =>
      downloadAuthenticated(
        `/orders/profit.csv?month=${month}`,
        `lucro-${month}.csv`,
      ),
    onError: (error) =>
      toast.add({
        type: "error",
        title: "Não foi possível exportar",
        description:
          error instanceof Error ? error.message : "Tente de novo em instantes.",
      }),
  });

  const data = report.data;

  return (
    <>
      <PageHeader
        title="Lucro"
        description={
          data
            ? data.orders > 0
              ? `${data.orders} pedidos pagos em ${formatYearMonth(month)}`
              : `Product Ads em ${formatYearMonth(month)}`
            : "Margem realizada por anúncio"
        }
        actions={
          <div className="flex items-center gap-2">
            <Popover>
              <PopoverTrigger
                render={
                  <button
                    type="button"
                    className={buttonVariants({ variant: "secondary" })}
                  >
                    <Calendar />
                    {formatYearMonth(month)}
                  </button>
                }
              />
              <PopoverContent align="end" className="w-60">
                <MonthGrid
                  value={month}
                  onValueChange={setMonth}
                  max={currentYearMonth()}
                  className="w-full"
                />
              </PopoverContent>
            </Popover>
            <Button
              variant="secondary"
              loading={exportCsv.isPending}
              onClick={() => exportCsv.mutate()}
            >
              <FileSpreadsheet />
              Exportar CSV
            </Button>
          </div>
        }
      />

      {report.isLoading ? (
        <div className="flex justify-center py-16">
          <Spinner className="size-5 text-ink-muted" />
        </div>
      ) : data && data.listings.length > 0 ? (
        <div className="flex flex-col gap-3">
          {data.readiness ? (
            <ProfitReadiness readiness={data.readiness} />
          ) : null}
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-3">
            <Metric
              label="Receita"
              value={formatBRL(data.revenue)}
              quality={data.quality}
            />
            <Metric
              label="Taxa de venda"
              value={formatBRL(data.fees)}
              quality={data.quality}
              hint={
                data.billedOrders > 0
                  ? "Do extrato do Mercado Livre quando importado"
                  : "Estimativa do pedido — importe o extrato em Conexões"
              }
            />
            <Metric
              label="Custo"
              value={formatBRL(data.cogs)}
              quality={data.quality}
            />
            <Metric
              label="Frete"
              value={formatBRL(data.shipping)}
              quality={data.quality}
            />
            <Metric
              label="Product Ads"
              value={formatBRL(data.ads ?? 0)}
              hint="Gasto do mês — importe em Conexões. Sem conta de Ads, fica zerado."
            />
            <Metric
              label="Lucro"
              value={formatBRL(data.profit)}
              quality={data.quality}
              emphasize
            />
          </div>
          <p className="text-2xs text-ink-muted">
            {data.quality === "partial"
              ? "Totais somam o que já sabemos — o que falta aparece como parcial na tabela."
              : data.billedOrders > 0
                ? `${data.billedOrders} pedido${data.billedOrders === 1 ? "" : "s"} conferido${data.billedOrders === 1 ? "" : "s"} no extrato`
                : "Lucro ainda estimado — importe o extrato em Conexões para conferir o que o ML cobrou"}
            {data.estimatedOrders > 0 && data.billedOrders > 0
              ? ` · ${data.estimatedOrders} ainda na estimativa`
              : ""}
            {data.divergentOrders > 0
              ? ` · ${data.divergentOrders} com diferença`
              : ""}
            {data.unallocated.count > 0
              ? ` · ${formatBRL(data.unallocated.amount)} sem pedido vinculado`
              : ""}
            {data.incompleteOrders > 0
              ? ` · ${data.incompleteOrders} sem taxa, custo ou frete`
              : ""}
            {Number(data.ads ?? 0) > 0
              ? ` · ${formatBRL(data.ads)} de Product Ads`
              : ""}
          </p>
          {data.divergences.length > 0 && (
            <Card>
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead>Pedido</TableHead>
                    <TableHead className="text-right">Estimado</TableHead>
                    <TableHead className="text-right">Extrato</TableHead>
                    <TableHead className="text-right">Diferença</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.divergences.map((row) => (
                    <TableRow key={row.orderId}>
                      <TableCell>
                        <Link
                          to="/orders/$id"
                          params={{ id: row.orderId }}
                          className="font-mono text-ink hover:text-ember"
                        >
                          {row.externalOrderId}
                        </Link>
                      </TableCell>
                      <TableCell className="text-right font-mono text-ink-muted">
                        {formatBRL(row.estimated)}
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {formatBRL(row.billed)}
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {formatBRL(row.delta)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          )}
          <Card>
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>ABC</TableHead>
                  <TableHead>Anúncio</TableHead>
                  <TableHead className="text-right">Receita</TableHead>
                  <TableHead className="text-right">Custos</TableHead>
                  <TableHead className="text-right">Ads</TableHead>
                  <TableHead className="text-right">Lucro</TableHead>
                  <TableHead className="text-right">Margem</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.listings.map((row) => (
                  <TableRow key={row.listingKey}>
                    <TableCell>
                      <Badge variant={ABC_VARIANT[row.abc] ?? "neutral"}>
                        {row.abc}
                      </Badge>
                    </TableCell>
                    <TableCell className="max-w-80">
                      <div className="flex flex-wrap items-center gap-1.5">
                        {row.listingId ? (
                          <Link
                            to="/listings/$id"
                            params={{ id: row.listingId }}
                            className="font-medium text-ink hover:text-ember"
                          >
                            {row.title}
                          </Link>
                        ) : (
                          <span className="font-medium text-ink">{row.title}</span>
                        )}
                        {!row.complete ? (
                          <Badge variant="warning">Parcial</Badge>
                        ) : null}
                      </div>
                      <p className="font-mono text-2xs text-ink-muted">
                        {row.listingKey}
                      </p>
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {formatBRL(row.revenue)}
                    </TableCell>
                    <TableCell className="text-right font-mono text-ink-muted">
                      {formatBRL(
                        Number(row.fees) +
                          Number(row.cogs) +
                          Number(row.shipping) +
                          Number("other" in row ? row.other : 0),
                      )}
                    </TableCell>
                    <TableCell className="text-right font-mono text-ink-muted">
                      {formatBRL(row.ads ?? 0)}
                    </TableCell>
                    <TableCell
                      className={cn(
                        "text-right font-mono",
                        row.complete ? "text-ink" : "text-ink-muted",
                      )}
                    >
                      {formatBRL(row.profit)}
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {formatPercent(row.margin)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {data?.readiness ? (
            <ProfitReadiness readiness={data.readiness} />
          ) : null}
          <Card>
            <EmptyState
              icon={<CircleDollarSign />}
              title="Nenhum pedido pago neste mês"
              description="O relatório usa pedidos pagos, enviados ou entregues. Cadastre o custo do SKU em Estoque, importe o extrato e os Product Ads em Conexões."
            />
          </Card>
        </div>
      )}
    </>
  );
}

function Metric({
  label,
  value,
  hint,
  quality,
  emphasize,
}: {
  label: string;
  value: string;
  hint?: string;
  quality?: "empty" | "partial" | "complete" | null;
  emphasize?: boolean;
}) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center justify-between gap-2">
          <p className="text-2xs text-ink-muted">
            {hint ? (
              <Tooltip content={hint}>
                <span className="cursor-help border-b border-dotted border-ink-faint">
                  {label}
                </span>
              </Tooltip>
            ) : (
              label
            )}
          </p>
          {emphasize ? <ProfitQualityBadge quality={quality} /> : null}
        </div>
        <p
          className={cn(
            "mt-1 font-mono font-semibold text-ink",
            emphasize ? "text-[1.0625rem]" : "text-[0.9375rem]",
          )}
        >
          {value}
        </p>
      </CardContent>
    </Card>
  );
}
